// PR-8 (frontend-rearchitecture, role-sweep-tier-1):
// The role-name-based getters below (`isSuperAdmin`, `isShopper`, etc.) are
// retained intentionally for this PR. New code (TheNavbar.vue, etc.) uses
// the capability-based `usePermissions().can(...)` helper from
// `@/composables/usePermissions`. Migrating these getters to capabilities
// would break ~15 existing call sites and is deferred to a tier-2 sweep.
//
// The `localStorage.setItem('roleName', ...)` writes in `login` and the
// matching `removeItem` in `logout` are STORAGE — they back the `roleName`
// fallback used by legacy code paths and must stay until those paths migrate.
import { createStore } from 'vuex'
import notification from './notification'
import reports from './reports'
import productModal from './productModal'
import storeEntity from './storeEntity'
import addOrder from './addOrder'
import { jsonapiModule } from 'jsonapi-vuex'
import axiosInstance from '../axios'
import { useAccountStore } from '../stores/account'
import { useClientsStore } from '../stores/clients'
import { useClientsWorkspaceStore } from '../stores/clientsWorkspace'
import { useApprovalWorkspaceStore } from '../stores/approvalWorkspace'
import { clearAnonymousStorefrontSession } from '../router/anonymousStorefrontSession'
import { clearSessionProvenance } from '../router/sessionProvenance'

export default createStore({
    state: {
        account: null,
        // The lines of the cart the client is working on: ONE store's cart,
        // as the last cart RPC for that store returned it. Carts are keyed by
        // (account, store) and the account payload carries none.
        cart: [],
        title: '',
        topTitle: false,
        toolbarColor: 'white',
        displayOwLogo: true,
        displayLogoutButton: false,
        backgroundColor: '#FFFFFF'
    },

    modules: {
        notification,
        reports,
        productModal,
        storeEntity,
        addOrder,
        jv: jsonapiModule(axiosInstance)
    },

    getters: {

        isSuperAdmin: state => {
            return state.account && state.account.role.name === 'Super Admin'
        },

        isShopper: state => {
            return state.account && state.account.role.name === 'Shopper'
        },

        // Route-fork PR-3: role rename swept the seeded role.name from
        // "Distributor Manager" -> "Owner" and "Admin Buyer" -> "Client
        // Admin". Getter names retained to avoid component-wide churn;
        // string comparisons updated to the new canonical names.
        isDistributorManager: state => {
            return state.account && state.account.role.name === 'Owner'
        },

        isSalesExecutive: state => {
            return state.account && state.account.role.name === 'Sales Executive'
        },

        isClientAdmin: state => {
            return state.account && state.account.role.name === 'Client Admin'
        },

        // Effective tenant id, impersonation-aware. Cascade order matters:
        //   1. Pinia useAccountStore.distributor.id — set by /auth/login and
        //      /auth/me, and updated on impersonation start/stop. This is
        //      the only source that reflects "SA is currently acting as
        //      distributor X" — state.account holds the SA's OWN identity
        //      (distributor_id = null), which is wrong for backend calls
        //      that need the impersonated tenant.
        //   2. state.account.distributor_id — legacy fallback for flows
        //      that don't hydrate Pinia (signup, password reset, page-
        //      reload re-auth in router/index.js) and for tests that build
        //      a Vuex store without Pinia.
        // `!= null` (not `!`) so id === 0 is treated as a real id —
        // defensive; not used today but cheap.
        getDistributorId: state => {
            const piniaId = useAccountStore().distributor?.id
            if (piniaId != null) return piniaId
            return state.account ? state.account.distributor_id : null
        },

        isLoggedIn: state => {
            return !!state.account
        },

        getBackgroundColor: state => {
            return state.backgroundColor
        }
    },

    mutations: {
        setAccount(state, account) {
            // Another account (or none) means another cart owner: the old
            // owner's lines must not linger in the client.
            const previousId = state.account?._jv?.id
            const nextId = account?._jv?.id
            if (nextId == null || String(nextId) !== String(previousId)) {
                state.cart = []
            }
            state.account = account
        },

        setCart(state, cart) {
            state.cart = Array.isArray(cart) ? cart : []
        },

    },

    actions: {
        // PR-11: dispatch supports two shapes for backwards compat.
        //   1. Legacy:    dispatch('login', accountObject)
        //   2. PR-11:     dispatch('login', { account, authResponse })
        //
        // The PR-11 shape additionally hydrates the Pinia useAccountStore
        // from the `/api/auth/login` response payload (`token`, `account_id`,
        // `capabilities`, `distributor`). Without this hydration, every
        // `requireCapability(...)` route guard denies by default because
        // useAccountStore.capabilities is empty — observed during PR-10
        // smoke testing on the rearch sandbox where SA could not reach
        // /admin-buyer/dashboard despite holding `client_portal.orders_read`.
        //
        // The legacy single-argument shape is preserved for call sites
        // that don't have the auth response on hand (signup, password
        // reset, page-reload re-auth in router/index.js). Those flows
        // remain unhydrated for now; a `/auth/me` endpoint is the
        // canonical follow-up to rehydrate on page reload (called out in
        // src/stores/account.js).
        login({ commit }, payload) {
            const account = (payload && payload.account)
                ? payload.account
                : payload
            const authResponse = (payload && payload.authResponse)
                ? payload.authResponse
                : null
            // GH #2941 defect 2. Did the caller actually collect credentials?
            //
            // Only the CALLER knows. This action is dispatched by three
            // kinds of path and they are not distinguishable from the
            // payload:
            //
            //   1. credentialed sign-in (TheLoginPage's form, signup,
            //      password reset, invitation accept, portal auto-login);
            //   2. the ANONYMOUS bootstrap, which swaps to the store's
            //      shared shopper with no credentials at all
            //      (StorefrontSessionGate.loginShopper,
            //      TheLoginPage.loginShopperFromURL);
            //   3. page-reload REHYDRATION (router/requireToken.js), which
            //      re-runs for whichever of the two the session already is.
            //
            // So the flag is opt-in and defaults to false: absent proof of
            // credentials we must not touch the marker. Failing the other
            // way is a data exposure, not a cosmetic bug -- see below.
            const credentialed = !!(payload && payload.credentialed)

            // Commit FIRST so state.account is populated even if downstream
            // localStorage writes throw — prevents null-account crashes in
            // components that mount immediately after the router guard
            // resolves (issue #859, AccountList.vue:125).
            commit('setAccount', account)

            localStorage.setItem('campaignId', account.campaign_id)
            localStorage.setItem('distributorId', account.distributor_id)
            localStorage.setItem('roleId', account.role_id)
            // Guard role: jsonapi-vuex may not have normalized the role
            // relationship if it wasn't included in the response.
            if (account.role && account.role.name) {
                localStorage.setItem('roleName', account.role.name)
            }

            if (credentialed) {
                // The anonymous-storefront marker describes the session
                // being REPLACED here, so it must not survive into this
                // one. Left behind, it makes a real shopper look like the
                // store's shared auto-provisioned account:
                // StorefrontLayout.hasPersonalSession is
                // `isLoggedIn && !isAnonymousStorefrontSession()`, so the
                // Profile / Orders / Addresses / Logout controls are
                // withheld from someone on their own account (GH #2941
                // defect 2). This is the sign-IN half of the pair whose
                // sign-OUT half lives in `logout` below.
                //
                // GATED, NOT UNCONDITIONAL, and the gate is load-bearing.
                // Non-credentialed paths dispatch this same action on every
                // page load -- the storefront session gate adopting a held
                // ANONYMOUS session on a reload of any shopper page
                // (router/ensureStorefrontSession.js), and requireToken's
                // back-office rehydration. Clearing unconditionally would
                // drop the marker on every refresh and hand an anonymous
                // visitor the shared account's saved addresses and order
                // history -- the exact exposure the marker exists to prevent.
                // See router/anonymousStorefrontSession.js.
                clearAnonymousStorefrontSession()
                // And the two records that decide whether the session's TOKEN
                // is treated as the anonymous cart session (kept in memory and
                // re-written after a logout; re-minted instead of /login on a
                // 401). A credentialed login that does not hydrate -- the
                // password-reset page dispatches with no auth response --
                // would otherwise leave the previous anonymous visit's answer
                // describing a real user's token (gate P1-1 on #3957). Every
                // credentialed path passes `credentialed: true`, so this is
                // the one choke point. A hydrating login re-records the
                // server's answer just below.
                clearSessionProvenance()
                useAccountStore().anonymousStorefront = false
            }

            if (authResponse) {
                useAccountStore().hydrate(authResponse)
            }
        },

        logout({ commit }) {
            localStorage.removeItem('campaignId')
            localStorage.removeItem('companyEmail')
            localStorage.removeItem('distributorId')
            localStorage.removeItem('firstName')
            localStorage.removeItem('lastName')
            localStorage.removeItem('roleId')
            localStorage.removeItem('roleName')
            localStorage.removeItem('selectedDepartment')
            localStorage.removeItem('selectedManager')
            localStorage.removeItem('selectedLocation')
            localStorage.removeItem('token')
            localStorage.removeItem('userId')
            // Clear the impersonation banner breadcrumb (IMPERSONATION_NAME_KEY
            // in stores/impersonation.js). stopImpersonation() removes it, but a
            // generic logout (token expiry, etc.) may not route through that, and
            // requireToken reads this key to skip the doomed self-account fetch on
            // an impersonation boot. Harmless if left (App.vue's boot-time
            // hydrate() self-heals it), but clearing on logout avoids a stale crumb.
            localStorage.removeItem('impersonationDistributorName')

            // The anonymous-storefront marker describes THIS session ("the
            // store's shared auto-provisioned shopper, not a person"), so it
            // must not outlive it. Left behind, it would mis-describe the
            // NEXT session: a real shopper who signs in on this tab would
            // still look anonymous and would be denied the Profile / Orders /
            // Addresses links to their own account.
            // See router/anonymousStorefrontSession.js.
            clearAnonymousStorefrontSession()

            commit('setAccount', null)

            // PR-11: clear Pinia useAccountStore so the next session
            // starts with no carry-over capabilities/token/distributor.
            useAccountStore().clear()

            // GH #4425: the cached CLIENT lists are tenant data. Logout is an
            // in-SPA route change (no reload), and ClientPicker only fetches
            // when its list is empty, so a stale list would show the previous
            // distributor's clients to whoever signs in next on this tab.
            useClientsWorkspaceStore().clear()
            useClientsStore().clear()
            // The approvals workspace caches GET /clients/ for its per-client
            // grouping -- the same client records, so the same exposure.
            useApprovalWorkspaceStore().clear()
        }
    }
})
